import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auto-logout after 8 hours of inactivity. Any of mouse move, click, key,
// touch, or scroll counts as activity. The last-activity timestamp survives
// tab refresh via localStorage so opening a new tab doesn't reset the clock.
const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000; // 8 hours (matches backend session timeout)
const IDLE_WARN_MS = 5 * 60 * 1000;        // toast 5 min before force-logout
const IDLE_STORAGE_KEY = "sre_last_activity";
const IDLE_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];

const AuthCtx = createContext({
  user: null,
  loading: true,
  modules: new Set(),
  hasModule: () => false,
  refresh: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState(() => new Set());

  const loadModules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/permissions/me`, { withCredentials: true });
      setModules(new Set(data?.effective_modules || []));
    } catch (_) {
      setModules(new Set());
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      await loadModules();
    } catch (e) {
      // 403 with detail=Account awaiting approval -> still keep "pending" info
      if (e?.response?.status === 403) {
        try {
          const { data } = await axios.get(`${API}/auth/whoami`, { withCredentials: true });
          setUser(data);
          setModules(new Set());
        } catch (_) {
          setUser(null);
          setModules(new Set());
        }
      } else {
        setUser(null);
        setModules(new Set());
      }
    } finally {
      setLoading(false);
    }
  }, [loadModules]);

  useEffect(() => {
    // If returning from OAuth callback (URL has session_id fragment), skip /me — AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout request failed", err);
    }
    try { localStorage.removeItem(IDLE_STORAGE_KEY); } catch (_) { /* noop */ }
    setUser(null);
    window.location.href = "/login";
  }, []);

  // -------- 4-hour idle auto-logout --------
  const warnedRef = useRef(false);
  useEffect(() => {
    if (!user) return undefined;

    const stamp = () => {
      try { localStorage.setItem(IDLE_STORAGE_KEY, String(Date.now())); } catch (_) { /* noop */ }
      warnedRef.current = false;
    };
    stamp();
    IDLE_EVENTS.forEach((ev) => window.addEventListener(ev, stamp, { passive: true }));

    const tick = setInterval(() => {
      let last = 0;
      try { last = Number(localStorage.getItem(IDLE_STORAGE_KEY) || "0"); } catch (_) { last = 0; }
      const idleFor = Date.now() - last;
      if (idleFor >= IDLE_LIMIT_MS) {
        clearInterval(tick);
        toast.error("You have been logged out due to 8 hours of inactivity.", { duration: 6000 });
        logout();
      } else if (idleFor >= IDLE_LIMIT_MS - IDLE_WARN_MS && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning("You will be logged out in 5 minutes due to inactivity. Move the mouse or press any key to stay logged in.", {
          duration: 15000,
        });
      }
    }, 30_000); // check every 30s — very cheap

    return () => {
      clearInterval(tick);
      IDLE_EVENTS.forEach((ev) => window.removeEventListener(ev, stamp));
    };
  }, [user, logout]);

  const hasModule = useCallback((key) => modules.has(key), [modules]);

  const value = useMemo(
    () => ({ user, loading, modules, hasModule, refresh, logout }),
    [user, loading, modules, hasModule, refresh, logout],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
